import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { ArrowLeft, Send, Paperclip, Download, User, Clock, Tag, FileText, X, UserCheck, UserPlus, Lock, MessageSquare, Activity, Plus, UserMinus, Merge, Eye, Mail, Trash2 } from 'lucide-react';
import api from '../../services/api';
import { toast } from 'react-toastify';
import io from 'socket.io-client';
import { SOCKET_URL, SOCKET_PATH, UPLOAD_URL } from '../../config';

const statusColors = {
  'Yeni': 'bg-blue-100 text-blue-700 border-blue-200',
  'İşleme Alındı': 'bg-purple-100 text-purple-700 border-purple-200',
  'Devam Ediyor': 'bg-amber-100 text-amber-700 border-amber-200',
  'Beklemede': 'bg-gray-100 text-gray-600 border-gray-200',
  'Logo\'ya İletildi': 'bg-indigo-100 text-indigo-700 border-indigo-200',
  'Müşteri Yanıtı Bekleniyor': 'bg-pink-100 text-pink-700 border-pink-200',
  'Çözüldü': 'bg-emerald-100 text-emerald-700 border-emerald-200',
  'Kapatıldı': 'bg-red-100 text-red-700 border-red-200',
};

const statuses = ['Yeni', 'İşleme Alındı', 'Devam Ediyor', 'Çözüldü', 'Logo\'ya İletildi'];

export default function StaffTicketDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [ticket, setTicket] = useState(null);
  const [message, setMessage] = useState('');
  const [isInternal, setIsInternal] = useState(false);
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const [loading, setLoading] = useState(true);
  const [staffList, setStaffList] = useState([]);
  const [showAssignModal, setShowAssignModal] = useState(false);
  const [tags, setTags] = useState([]);
  const [allTags, setAllTags] = useState([]);
  const [activityLog, setActivityLog] = useState([]);
  const [watchers, setWatchers] = useState([]);
  const [watcherEmail, setWatcherEmail] = useState('');
  const [cannedResponses, setCannedResponses] = useState([]);
  const [showCanned, setShowCanned] = useState(false);
  const [showActivity, setShowActivity] = useState(false);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    loadTicket();
    loadStaff();
    loadTags();
    loadActivity();
    loadWatchers();
    loadCannedResponses();
    const socket = io(SOCKET_URL, { path: SOCKET_PATH });
    socket.emit('joinRoom', { userId: user?.id, ticketId: id });
    socket.on('newMessage', () => loadTicket());
    socket.on('ticketStatusChanged', (data) => { if (String(data.ticketId) === id) loadTicket(); });
    socket.on('ticketClaimed', (data) => { if (String(data.ticketId) === id) loadTicket(); });
    socket.on('ticketAssigned', (data) => { if (String(data.ticketId) === id) loadTicket(); });
    return () => socket.disconnect();
  }, [id, user]);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticket?.messages]);

  const loadTicket = async () => {
    try {
      const res = await api.get(`/tickets/${id}`);
      setTicket(res.data);
    } catch (err) { toast.error('Ticket yüklenemedi'); }
    setLoading(false);
  };

  const loadStaff = async () => {
    try { const res = await api.get('/users/staff'); setStaffList(res.data); } catch (err) {}
  };

  const loadTags = async () => {
    try {
      const [ticketTags, all] = await Promise.all([api.get(`/tags/ticket/${id}`), api.get('/tags')]);
      setTags(ticketTags.data); setAllTags(all.data);
    } catch (err) {}
  };

  const loadActivity = async () => {
    try { const res = await api.get(`/activity/ticket/${id}`); setActivityLog(res.data); } catch (err) {}
  };

  const loadWatchers = async () => {
    try { const res = await api.get(`/tickets/${id}/watchers`); setWatchers(res.data); } catch (err) {}
  };

  const loadCannedResponses = async () => {
    try { const res = await api.get('/canned-responses'); setCannedResponses(res.data); } catch (err) {}
  };

  const addTag = async (tagId) => {
    await api.post(`/tags/ticket/${id}`, { tagId }); loadTags();
  };

  const removeTag = async (tagId) => {
    await api.delete(`/tags/ticket/${id}/${tagId}`); loadTags();
  };

  const addWatcher = async () => {
    if (!watcherEmail) return;
    await api.post(`/tickets/${id}/watchers`, { email: watcherEmail });
    setWatcherEmail(''); loadWatchers(); toast.success('Takipçi eklendi');
  };

  const removeWatcher = async (wId) => {
    await api.delete(`/tickets/${id}/watchers/${wId}`); loadWatchers();
  };

  const useCannedResponse = (content) => {
    // Resolve variables
    const resolved = content
      .replace(/\{\{musteri_adi\}\}/g, ticket?.ContactName || ticket?.CreatedByName || '')
      .replace(/\{\{ticket_no\}\}/g, `#${ticket?.Id}`)
      .replace(/\{\{ticket_konu\}\}/g, ticket?.Title || '')
      .replace(/\{\{personel_adi\}\}/g, user?.fullName || '')
      .replace(/\{\{tarih\}\}/g, new Date().toLocaleDateString('tr-TR'))
      .replace(/\{\{firma_adi\}\}/g, ticket?.CustomerName || '')
      .replace(/\{\{kategori\}\}/g, ticket?.Category || '')
      .replace(/\{\{oncelik\}\}/g, ticket?.Priority || '');
    setMessage(resolved);
    setShowCanned(false);
  };

  const handleStatusChange = async (newStatus) => {
    try {
      await api.put(`/tickets/${id}/status`, { status: newStatus });
      toast.success(`Durum güncellendi: ${newStatus}`);
      loadTicket();
    } catch (err) { toast.error('Durum güncellenemedi'); }
  };

  const handleClaim = async () => {
    try {
      await api.put(`/tickets/${id}/claim`);
      toast.success('Ticket üstlenildi!');
      loadTicket();
    } catch (err) { toast.error('İşlem başarısız'); }
  };

  const handleAssign = async (staffId) => {
    try {
      await api.put(`/tickets/${id}/assign`, { assignedTo: staffId });
      toast.success('Ticket atandı!');
      setShowAssignModal(false);
      loadTicket();
    } catch (err) { toast.error('Atama başarısız'); }
  };

  const handleSend = async (e) => {
    e.preventDefault();
    if (!message.trim() && files.length === 0) return;
    setSending(true);
    try {
      const formData = new FormData();
      formData.append('message', message);
      formData.append('isInternal', isInternal);
      files.forEach(f => formData.append('files', f));
      await api.post(`/tickets/${id}/messages`, formData, { headers: { 'Content-Type': 'multipart/form-data' } });
      setMessage('');
      setFiles([]);
      loadTicket();
    } catch (err) { toast.error('Mesaj gönderilemedi'); }
    setSending(false);
  };

  if (loading) return <div className="flex justify-center py-20"><div className="animate-spin rounded-full h-10 w-10 border-b-2 border-blue-600" /></div>;
  if (!ticket) return <div className="text-center py-20 text-slate-500">Ticket bulunamadı</div>;

  return (
    <div className="max-w-6xl mx-auto">
      {/* Header */}
      <div className="flex items-start gap-3 mb-6">
        <Link to="/staff/tickets" className="p-2 text-slate-400 hover:text-slate-600 hover:bg-slate-100 rounded-lg mt-1"><ArrowLeft size={20} /></Link>
        <div className="flex-1">
          <div className="flex items-center gap-3 flex-wrap">
            <span className="text-sm font-mono text-slate-400">#{ticket.Id}</span>
            <h1 className="text-xl font-bold text-slate-800">{ticket.Title}</h1>
          </div>
          <div className="flex items-center gap-3 mt-1.5 flex-wrap">
            <span className={`text-xs px-3 py-1 rounded-full font-medium border ${statusColors[ticket.Status] || 'bg-slate-100 text-slate-600 border-slate-200'}`}>{ticket.Status}</span>
            <span className="text-xs text-slate-500 flex items-center gap-1"><Tag size={12} />{ticket.Category}</span>
            {(ticket.ContactName || ticket.CreatedByName) && (
              <span className="text-xs font-semibold text-orange-700 bg-orange-50 px-2 py-0.5 rounded-full border border-orange-200 flex items-center gap-1"><User size={12} />{ticket.ContactName || ticket.CreatedByName}</span>
            )}
            {ticket.CustomerName && (
              <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-2 py-0.5 rounded-full border border-emerald-200">{ticket.CustomerName}</span>
            )}
            <span className="text-xs text-slate-500 flex items-center gap-1"><Clock size={12} />{new Date(ticket.CreatedAt).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}</span>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Messages */}
        <div className="lg:col-span-2 space-y-4">
          {ticket.Description && (
            <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
              <h3 className="text-sm font-semibold text-slate-700 mb-2">Açıklama</h3>
              <p className="text-sm text-slate-600 whitespace-pre-wrap">{ticket.Description}</p>
              {ticket.files?.filter(f => !f.MessageId).length > 0 && (
                <div className="mt-3 pt-3 border-t border-slate-100 space-y-1.5">
                  {ticket.files.filter(f => !f.MessageId).map(file => (
                    <a key={file.Id} href={`${file.FilePath.replace('/uploads', UPLOAD_URL)}`} target="_blank" rel="noopener noreferrer"
                      className="flex items-center gap-2 p-2 bg-slate-50 rounded-lg hover:bg-blue-50 transition-colors group">
                      <Download size={14} className="text-slate-400 group-hover:text-blue-600" />
                      <span className="text-xs text-slate-600 truncate group-hover:text-blue-600">{file.OriginalName}</span>
                    </a>
                  ))}
                </div>
              )}
            </div>
          )}

          <div className="bg-white rounded-xl shadow-sm border border-slate-100 overflow-hidden">
            <div className="px-5 py-3 bg-slate-50 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-sm font-semibold text-slate-700">Mesajlar & Notlar</h3>
            </div>
            <div className="p-5 space-y-4 max-h-[500px] overflow-y-auto">
              {ticket.messages?.length === 0 ? (
                <p className="text-center text-slate-400 py-6 text-sm">Henüz mesaj yok</p>
              ) : ticket.messages?.map(msg => {
                const isCustomerMsg = !msg.UserRole || msg.UserRole === 'customer';
                const isStaffMsg = !isCustomerMsg;
                return (
                <div key={msg.Id} className={`flex ${msg.IsInternal ? 'justify-center' : isStaffMsg ? 'justify-end' : 'justify-start'}`}>
                  <div className={`max-w-[80%] rounded-2xl px-4 py-3 ${
                    msg.IsInternal
                      ? 'bg-amber-50 border-2 border-amber-200 text-slate-800'
                      : isCustomerMsg
                        ? 'bg-slate-100 text-slate-800'
                        : 'bg-blue-600 text-white'
                  }`}>
                    <div className="flex items-center gap-2 mb-1">
                      {msg.IsInternal && <Lock size={12} className="text-amber-600" />}
                      <span className={`text-xs font-semibold ${
                        msg.IsInternal ? 'text-amber-700' : isCustomerMsg ? 'text-slate-500' : 'text-blue-100'
                      }`}>
                        {msg.IsInternal ? `${msg.UserName || 'Sistem'} (İç DSY Yazışma)` : isCustomerMsg ? `${ticket.ContactName || msg.UserName || 'Müşteri'}` : `${msg.UserName} (Destek)`}
                      </span>
                    </div>
                    <p className="text-sm whitespace-pre-wrap">{msg.Message}</p>
                    {ticket.files?.filter(f => f.MessageId === msg.Id).length > 0 && (
                      <div className="mt-2 space-y-1">
                        {ticket.files.filter(f => f.MessageId === msg.Id).map(file => (
                          <a key={file.Id} href={`${file.FilePath.replace('/uploads', UPLOAD_URL)}`} target="_blank" rel="noopener noreferrer"
                            className={`flex items-center gap-1.5 text-xs px-2 py-1 rounded-md ${isCustomerMsg ? 'bg-white/60 text-blue-600 hover:bg-white' : 'bg-white/20 text-blue-100 hover:bg-white/30'}`}>
                            <Download size={12} />{file.OriginalName}
                          </a>
                        ))}
                      </div>
                    )}
                    <p className={`text-xs mt-1 ${msg.IsInternal ? 'text-amber-500' : isCustomerMsg ? 'text-slate-400' : 'text-blue-200'}`}>
                      {new Date(msg.CreatedAt).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}
                    </p>
                  </div>
                </div>
                );
              })}
              <div ref={messagesEndRef} />
            </div>

            {/* Send Message */}
            <form onSubmit={handleSend} className="border-t border-slate-100 p-4">
              {/* Internal toggle */}
              <div className="flex items-center gap-4 mb-3">
                <button type="button" onClick={() => setIsInternal(false)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${!isInternal ? 'bg-blue-100 text-blue-700 ring-2 ring-blue-300' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                  <MessageSquare size={14} /> Müşteriye Yanıt
                </button>
                <button type="button" onClick={() => setIsInternal(true)}
                  className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${isInternal ? 'bg-amber-100 text-amber-700 ring-2 ring-amber-300' : 'bg-slate-100 text-slate-500 hover:bg-slate-200'}`}>
                  <Lock size={14} /> İç DSY Yazışma
                </button>
              </div>

              <p className="text-[11px] text-slate-400 mb-2">📋 Ekran görüntüsünü <kbd className="px-1 py-0.5 bg-slate-100 rounded font-mono text-[10px]">Ctrl+V</kbd> ile yapıştırabilirsiniz</p>

              {files.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-3">
                  {files.map((f, i) => (
                    <span key={i} className="inline-flex items-center gap-1 bg-blue-50 text-blue-700 text-xs px-2 py-1 rounded-lg">
                      <FileText size={12} />{f.name}
                      <button type="button" onClick={() => setFiles(prev => prev.filter((_, idx) => idx !== i))}><X size={12} /></button>
                    </span>
                  ))}
                </div>
              )}
              {/* Canned Responses */}
              <div className="relative mb-2">
                <button type="button" onClick={() => setShowCanned(!showCanned)}
                  className="text-xs text-blue-600 hover:text-blue-700 font-medium flex items-center gap-1">
                  <MessageSquare size={12} /> Hazır Yanıt Kullan
                </button>
                {showCanned && (() => {
                  const ticketCat = ticket?.Category || '';
                  const matched = cannedResponses.filter(cr => cr.Category === ticketCat);
                  const general = cannedResponses.filter(cr => cr.Category === 'Genel' && cr.Category !== ticketCat);
                  const others = cannedResponses.filter(cr => cr.Category !== ticketCat && cr.Category !== 'Genel');
                  const sorted = [...matched, ...general, ...others];
                  return (
                  <div className="absolute bottom-full left-0 mb-2 bg-white border border-slate-200 rounded-xl shadow-xl z-20 w-80 max-h-60 overflow-y-auto">
                    {sorted.map(cr => (
                      <button key={cr.Id} type="button" onClick={() => useCannedResponse(cr.Content)}
                        className="w-full text-left px-4 py-2.5 hover:bg-blue-50 border-b border-slate-100 last:border-0">
                        <div className="flex items-center gap-2">
                          <p className="text-xs font-semibold text-slate-700 flex-1">{cr.Title}</p>
                          <span className={`text-[10px] px-1.5 py-0.5 rounded ${cr.Category === ticketCat ? 'bg-blue-100 text-blue-600' : 'bg-slate-100 text-slate-400'}`}>{cr.Category}</span>
                        </div>
                        <p className="text-xs text-slate-400 line-clamp-1 mt-0.5">{cr.Content.substring(0, 60)}...</p>
                      </button>
                    ))}
                    {sorted.length === 0 && <p className="text-xs text-slate-400 p-4">Henüz şablon yok</p>}
                  </div>
                  );
                })()}
              </div>

              <div className="flex items-end gap-2">
                <div className="flex-1">
                  <textarea value={message} onChange={e => setMessage(e.target.value)} rows={5}
                    onPaste={e => {
                      const items = e.clipboardData?.items;
                      if (!items) return;
                      for (const item of items) {
                        if (item.type.startsWith('image/')) {
                          e.preventDefault();
                          const file = item.getAsFile();
                          if (file) {
                            const named = new File([file], `screenshot-${Date.now()}.png`, { type: file.type });
                            setFiles(prev => [...prev, named].slice(0, 5));
                          }
                        }
                      }
                    }}
                    className={`w-full px-4 py-2.5 border rounded-xl focus:outline-none focus:ring-2 text-slate-800 placeholder-slate-400 resize-y min-h-[120px] text-sm ${
                      isInternal ? 'border-amber-200 focus:ring-amber-400 bg-amber-50/50' : 'border-slate-200 focus:ring-blue-500'
                    }`}
                    placeholder={isInternal ? 'İç DSY yazışma (müşteri göremez)...' : 'Müşteriye yanıt yazın...'} />
                </div>
                <label className="p-2.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-xl cursor-pointer transition-colors">
                  <Paperclip size={20} />
                  <input type="file" multiple onChange={e => setFiles(prev => [...prev, ...Array.from(e.target.files)].slice(0, 5))} className="hidden" />
                </label>
                <button type="submit" disabled={sending}
                  className={`p-2.5 text-white rounded-xl transition-colors disabled:opacity-50 ${isInternal ? 'bg-amber-500 hover:bg-amber-600' : 'bg-blue-600 hover:bg-blue-700'}`}>
                  {sending ? <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white" /> : <Send size={20} />}
                </button>
              </div>
            </form>
          </div>
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Actions */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 space-y-3">
            <h3 className="text-sm font-semibold text-slate-700">İşlemler</h3>

            {/* Claim */}
            {!ticket.AssignedTo && (
              <button onClick={handleClaim} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-emerald-600 text-white rounded-xl font-medium hover:bg-emerald-700 transition-colors text-sm">
                <UserCheck size={18} /> Üstüme Al
              </button>
            )}

            {/* Assign */}
            <button onClick={() => setShowAssignModal(true)} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-blue-600 text-white rounded-xl font-medium hover:bg-blue-700 transition-colors text-sm">
              <UserPlus size={18} /> {ticket.AssignedTo ? 'Yeniden Ata' : 'Personele Ata'}
            </button>

            {/* Status Change */}
            <div>
              <label className="block text-xs font-medium text-slate-500 mb-1.5">Durum Değiştir</label>
              <select value={ticket.Status} onChange={e => handleStatusChange(e.target.value)}
                className="w-full px-3 py-2 border border-slate-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-500">
                {statuses.map(s => <option key={s} value={s}>{s}</option>)}
              </select>
              <button onClick={async () => {
                try {
                  await api.post(`/tickets/${id}/notify-status`);
                  toast.success('Müşteriye durum bilgilendirmesi gönderildi!');
                } catch (err) { toast.error('Mail gönderilemedi'); }
              }}
                className="mt-2 w-full flex items-center justify-center gap-1.5 px-3 py-2 bg-emerald-50 text-emerald-700 border border-emerald-200 rounded-xl text-xs font-medium hover:bg-emerald-100 transition-colors">
                <Mail size={13} /> Bilgilendirme Gönder
              </button>
            </div>
          </div>

          {/* Info */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5 space-y-4">
            <h3 className="text-sm font-semibold text-slate-700">Talep Bilgileri</h3>
            <div className="space-y-3">
              <div>
                <p className="text-xs text-slate-400">Durum</p>
                <span className={`inline-block text-xs px-3 py-1 rounded-full font-medium border mt-1 ${statusColors[ticket.Status] || ''}`}>{ticket.Status}</span>
              </div>
              <div>
                <p className="text-xs text-slate-400">Kategori</p>
                <p className="text-sm font-medium text-slate-700 mt-0.5">{ticket.Category}</p>
              </div>
              <div>
                <p className="text-xs text-slate-400">Öncelik</p>
                <p className="text-sm font-medium text-slate-700 mt-0.5">{ticket.Priority}</p>
              </div>
              <div className="bg-slate-50 rounded-lg p-3 border border-slate-200">
                <p className="text-xs text-slate-400 mb-1.5">Talep Sahibi</p>
                <p className="text-sm font-semibold text-slate-800">{ticket.ContactName || ticket.CreatedByName || '-'}</p>
                <p className="text-xs text-blue-600 mt-0.5">{ticket.ContactEmail || ticket.CreatedByEmail || '-'}</p>
                {ticket.CustomerName && (
                  <p className="text-xs text-emerald-600 font-medium mt-1 flex items-center gap-1">
                    <span className="w-3 h-3 bg-emerald-500 rounded-sm flex items-center justify-center text-white text-[8px]">F</span>
                    {ticket.CustomerName}
                  </p>
                )}
              </div>
              {ticket.AssignedToName && (
                <div>
                  <p className="text-xs text-slate-400">Atanan Personel</p>
                  <div className="flex items-center gap-2 mt-1">
                    <div className="w-6 h-6 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center text-white text-xs font-bold">
                      {ticket.AssignedToName.charAt(0)}
                    </div>
                    <p className="text-sm font-medium text-slate-700">{ticket.AssignedToName}</p>
                  </div>
                </div>
              )}
              <div>
                <p className="text-xs text-slate-400">Oluşturulma</p>
                <p className="text-sm text-slate-700 mt-0.5">{new Date(ticket.CreatedAt).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}</p>
              </div>
            </div>
          </div>

          {/* Tags */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Etiketler</h3>
            <div className="flex flex-wrap gap-1.5 mb-2">
              {tags.map(t => (
                <span key={t.Id} className="inline-flex items-center gap-1 text-xs px-2 py-1 rounded-full font-medium" style={{ backgroundColor: t.Color + '20', color: t.Color }}>
                  {t.Name}
                  <button onClick={() => removeTag(t.Id)} className="hover:opacity-70"><X size={10} /></button>
                </span>
              ))}
            </div>
            <select onChange={e => { if (e.target.value) { addTag(parseInt(e.target.value)); e.target.value = ''; } }}
              className="w-full px-3 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-blue-500">
              <option value="">Etiket ekle...</option>
              {allTags.filter(t => !tags.find(tt => tt.Id === t.Id)).map(t => <option key={t.Id} value={t.Id}>{t.Name}</option>)}
            </select>
          </div>

          {/* Watchers/CC */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
            <h3 className="text-sm font-semibold text-slate-700 mb-2">Takipçiler (CC)</h3>
            <div className="space-y-1.5 mb-2">
              {watchers.map(w => (
                <div key={w.Id} className="flex items-center justify-between text-xs bg-slate-50 px-2.5 py-1.5 rounded-lg">
                  <span className="text-slate-700">{w.Name ? `${w.Name} (${w.Email})` : w.Email}</span>
                  <button onClick={() => removeWatcher(w.Id)} className="text-slate-400 hover:text-red-500"><X size={12} /></button>
                </div>
              ))}
              {watchers.length === 0 && <p className="text-xs text-slate-400">Takipçi yok</p>}
            </div>
            <div className="space-y-1.5">
              <select onChange={e => {
                if (!e.target.value) return;
                const s = staffList.find(st => st.Id === parseInt(e.target.value));
                if (s && !watchers.find(w => w.Email === s.Email)) {
                  api.post(`/tickets/${id}/watchers`, { email: s.Email, name: s.FullName }).then(() => { loadWatchers(); toast.success('Personel CC\'ye eklendi'); });
                }
                e.target.value = '';
              }} className="w-full px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-blue-500">
                <option value="">Personelden ekle...</option>
                {staffList.filter(s => !watchers.find(w => w.Email === s.Email)).map(s => (
                  <option key={s.Id} value={s.Id}>{s.FullName} ({s.Email})</option>
                ))}
              </select>
              <div className="flex gap-1.5">
                <input type="email" value={watcherEmail} onChange={e => setWatcherEmail(e.target.value)}
                  className="flex-1 px-2.5 py-1.5 border border-slate-200 rounded-lg text-xs focus:outline-none focus:ring-1 focus:ring-blue-500" placeholder="Harici e-posta ekle..." />
                <button onClick={addWatcher} className="px-2.5 py-1.5 bg-blue-600 text-white rounded-lg text-xs hover:bg-blue-700"><Plus size={12} /></button>
              </div>
            </div>
          </div>

          {/* Activity Log */}
          <div className="bg-white rounded-xl shadow-sm border border-slate-100 p-5">
            <button onClick={() => setShowActivity(!showActivity)} className="flex items-center gap-2 text-sm font-semibold text-slate-700 w-full">
              <Activity size={14} /> Aktivite Logu ({activityLog.length})
            </button>
            {showActivity && (
              <div className="mt-3 space-y-2 max-h-48 overflow-y-auto">
                {activityLog.map(a => (
                  <div key={a.Id} className="flex items-start gap-2 text-xs">
                    <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 flex-shrink-0" />
                    <div>
                      <span className="font-medium text-slate-700">{a.UserName || 'Sistem'}</span>
                      <span className="text-slate-500"> - {a.Detail || a.Action}</span>
                      <p className="text-slate-400">{new Date(a.CreatedAt).toLocaleString('tr-TR', { timeZone: 'Europe/Istanbul' })}</p>
                    </div>
                  </div>
                ))}
                {activityLog.length === 0 && <p className="text-xs text-slate-400">Kayıt yok</p>}
              </div>
            )}
          </div>

          {/* Delete Ticket */}
          <div className="bg-white rounded-xl shadow-sm border border-red-100 p-5">
            <button onClick={async () => {
              if (!window.confirm('Bu ticket tamamen silinecek. Emin misiniz?')) return;
              try {
                await api.delete(`/tickets/${id}`);
                toast.success('Ticket silindi');
                navigate('/staff/tickets');
              } catch (err) { toast.error('Silinemedi'); }
            }} className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-red-50 text-red-600 border border-red-200 rounded-xl text-sm font-medium hover:bg-red-100 transition-colors">
              <Trash2 size={15} /> Ticket'ı Sil
            </button>
          </div>

        </div>
      </div>

      {/* Assign Modal */}
      {showAssignModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-md">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold text-slate-800">Personele Ata</h3>
              <button onClick={() => setShowAssignModal(false)} className="p-1 text-slate-400 hover:text-slate-600"><X size={20} /></button>
            </div>
            <div className="p-4 space-y-2 max-h-80 overflow-y-auto">
              {staffList.map(staff => (
                <button key={staff.Id} onClick={() => handleAssign(staff.Id)}
                  className={`w-full flex items-center gap-3 p-3 rounded-xl hover:bg-blue-50 transition-colors text-left ${ticket.AssignedTo === staff.Id ? 'bg-blue-50 ring-2 ring-blue-300' : 'bg-slate-50'}`}>
                  <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold">
                    {staff.FullName.charAt(0)}
                  </div>
                  <div>
                    <p className="text-sm font-medium text-slate-800">{staff.FullName}</p>
                    <p className="text-xs text-slate-500">{staff.Email} • {staff.Role === 'admin' ? 'Yönetici' : 'Personel'}</p>
                  </div>
                  {ticket.AssignedTo === staff.Id && <span className="ml-auto text-xs text-blue-600 font-medium">Mevcut</span>}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
